import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Accordion, AccordionDetails, AccordionSummary,
  Alert, Autocomplete, Box, Button, Card, CardContent, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, FormControl, Grid, IconButton, InputLabel, Menu, MenuItem, Paper, Select,
  Stack, TextField, Tooltip, Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  ArrowDownward as ArrowDownwardIcon,
  ExpandMore as ExpandMoreIcon,
  ArrowUpward as ArrowUpwardIcon,
  Delete as DeleteIcon,
  LocalOffer as HandicapIcon,
} from "@mui/icons-material";
import { alpha, useTheme } from "@mui/material/styles";
import { useNavigate, useParams } from "react-router-dom";
import { AmericanaHandicapType, AmericanaMatch, AmericanaRound, AmericanaStanding, Player, Tournament } from "../types";
import { apiRequest } from "../config/api";

// Local editable shape for a round's matches while the schedule is being built —
// pair slots start empty (playerId: "") until the admin assigns all four players.
type DraftMatch = {
  courtNumber: number;
  pair1Player1: string;
  pair1Player2: string;
  pair2Player1: string;
  pair2Player2: string;
};

type CoverageAnalysisResult = {
  totalPlayers: number;
  playersMeetingEveryone: number;
  unassignedSlots: number;
  missingByPlayer: Array<{ playerId: string; missingPlayerIds: string[] }>;
};

type AmericanaTemplateMatch = {
  courtNumber: number;
  pair1: [number, number];
  pair2: [number, number];
};

type MatchBalanceInfo = {
  label: "Balanced" | "Stronger Side" | "Much Stronger Side";
  strongerTeam: "A" | "B" | null;
  diff: number;
  color: "success" | "warning" | "error";
};

const AMERICANA_5X4_TEMPLATE: AmericanaTemplateMatch[][] = [
  [
    { courtNumber: 1, pair1: [1, 2], pair2: [3, 4] },
    { courtNumber: 2, pair1: [5, 6], pair2: [7, 8] },
    { courtNumber: 3, pair1: [9, 10], pair2: [11, 12] },
    { courtNumber: 4, pair1: [13, 14], pair2: [15, 16] },
  ],
  [
    { courtNumber: 1, pair1: [1, 8], pair2: [11, 16] },
    { courtNumber: 2, pair1: [2, 7], pair2: [12, 13] },
    { courtNumber: 3, pair1: [3, 5], pair2: [10, 15] },
    { courtNumber: 4, pair1: [4, 6], pair2: [9, 14] },
  ],
  [
    { courtNumber: 1, pair1: [1, 14], pair2: [5, 12] },
    { courtNumber: 2, pair1: [3, 9], pair2: [8, 13] },
    { courtNumber: 3, pair1: [2, 11], pair2: [6, 15] },
    { courtNumber: 4, pair1: [4, 10], pair2: [7, 16] },
  ],
  [
    { courtNumber: 1, pair1: [1, 13], pair2: [6, 10] },
    { courtNumber: 2, pair1: [3, 14], pair2: [7, 11] },
    { courtNumber: 3, pair1: [2, 16], pair2: [5, 9] },
    { courtNumber: 4, pair1: [4, 15], pair2: [8, 12] },
  ],
  [
    { courtNumber: 1, pair1: [1, 9], pair2: [7, 15] },
    { courtNumber: 2, pair1: [2, 10], pair2: [8, 14] },
    { courtNumber: 3, pair1: [4, 11], pair2: [5, 13] },
    { courtNumber: 4, pair1: [3, 12], pair2: [6, 16] },
  ],
];

function emptyDraftRounds(roundCount: number, courtCount: number): DraftMatch[][] {
  return Array.from({ length: roundCount }, () =>
    Array.from({ length: courtCount }, (_, i) => ({
      courtNumber: i + 1,
      pair1Player1: "",
      pair1Player2: "",
      pair2Player1: "",
      pair2Player2: "",
    }))
  );
}

function shuffle<T>(items: T[]): T[] {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

function draftRoundsFromSavedRounds(rounds: AmericanaRound[]): DraftMatch[][] {
  return rounds
    .slice()
    .sort((a, b) => a.roundNumber - b.roundNumber)
    .map(round =>
      round.matches
        .slice()
        .sort((a, b) => a.courtNumber - b.courtNumber)
        .map(match => ({
          courtNumber: match.courtNumber,
          pair1Player1: match.pair1.player1Id,
          pair1Player2: match.pair1.player2Id,
          pair2Player1: match.pair2.player1Id,
          pair2Player2: match.pair2.player2Id,
        }))
    );
}

function analyzeMeetingCoverage(draftRounds: DraftMatch[][], rosterIds: string[]): CoverageAnalysisResult {
  const metMap = new Map<string, Set<string>>();
  const rosterSet = new Set(rosterIds);

  rosterIds.forEach(id => metMap.set(id, new Set<string>()));

  let unassignedSlots = 0;

  draftRounds.forEach(round => {
    round.forEach(match => {
      const players = [
        match.pair1Player1,
        match.pair1Player2,
        match.pair2Player1,
        match.pair2Player2,
      ].filter(Boolean);

      unassignedSlots += 4 - players.length;

      // Any two players in the same match are considered to have met
      for (let i = 0; i < players.length; i += 1) {
        for (let j = i + 1; j < players.length; j += 1) {
          const a = players[i];
          const b = players[j];
          if (!rosterSet.has(a) || !rosterSet.has(b)) continue;
          metMap.get(a)?.add(b);
          metMap.get(b)?.add(a);
        }
      }
    });
  });

  const missingByPlayer = rosterIds
    .map(playerId => {
      const met = metMap.get(playerId) || new Set<string>();
      const missingPlayerIds = rosterIds.filter(otherId => otherId !== playerId && !met.has(otherId));
      return { playerId, missingPlayerIds };
    })
    .filter(item => item.missingPlayerIds.length > 0);

  return {
    totalPlayers: rosterIds.length,
    playersMeetingEveryone: rosterIds.length - missingByPlayer.length,
    unassignedSlots,
    missingByPlayer,
  };
}

function parseTimeToMinutes(timeStr?: string): number {
  if (!timeStr || !timeStr.includes(":")) return 9 * 60;
  const [h, m] = timeStr.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return 9 * 60;
  return h * 60 + m;
}

function minutesToTimeLabel(totalMinutes: number): string {
  const minutesInDay = ((totalMinutes % (24 * 60)) + (24 * 60)) % (24 * 60);
  const h = Math.floor(minutesInDay / 60).toString().padStart(2, "0");
  const m = (minutesInDay % 60).toString().padStart(2, "0");
  return `${h}:${m}`;
}

function parseAnyTimeToMinutes(value?: string): number | null {
  if (!value) return null;

  // HH:mm
  if (/^\d{1,2}:\d{2}$/.test(value)) {
    const [h, m] = value.split(":").map(Number);
    if (!Number.isNaN(h) && !Number.isNaN(m)) return h * 60 + m;
  }

  // ISO datetime or date-like string
  const dt = new Date(value);
  if (!Number.isNaN(dt.getTime())) {
    return dt.getHours() * 60 + dt.getMinutes();
  }

  // Fallback: extract time from strings like 2026-07-14T09:30:00
  const timeMatch = value.match(/(\d{1,2}):(\d{2})/);
  if (timeMatch) {
    const h = Number(timeMatch[1]);
    const m = Number(timeMatch[2]);
    if (!Number.isNaN(h) && !Number.isNaN(m)) return h * 60 + m;
  }

  return null;
}

function AmericanaTournamentAdmin() {
  const { tournamentId } = useParams<{ tournamentId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();

  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [players, setPlayers] = useState<Player[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Roster picker state (used until americanaPlayerIds is saved)
  const [rosterDraft, setRosterDraft] = useState<Player[]>([]);

  // Schedule builder state (used until americanaRounds is saved)
  const [roundCount, setRoundCount] = useState(5);
  const [courtCount, setCourtCount] = useState(4);
  const [draftRounds, setDraftRounds] = useState<DraftMatch[][]>([]);
  const [isEditingSchedule, setIsEditingSchedule] = useState(false);
  const [editDraftRounds, setEditDraftRounds] = useState<DraftMatch[][]>([]);
  const [coverageAnalysis, setCoverageAnalysis] = useState<CoverageAnalysisResult | null>(null);

  // Score entry state, keyed by `${roundNumber}-${courtNumber}`
  const [scoreDrafts, setScoreDrafts] = useState<Record<string, { pair1Games: string; pair2Games: string }>>({});
  const [strengthOrderIds, setStrengthOrderIds] = useState<string[]>([]);
  const [savingStrengthOrder, setSavingStrengthOrder] = useState(false);

  // Handicap catalog + per-match assignment state
  const [handicapCatalogDraft, setHandicapCatalogDraft] = useState<AmericanaHandicapType[]>([]);
  const [savingHandicapTypes, setSavingHandicapTypes] = useState(false);
  const [newHandicapLabel, setNewHandicapLabel] = useState("");
  const [newHandicapEmoji, setNewHandicapEmoji] = useState("");
  const [handicapMenu, setHandicapMenu] = useState<{
    anchorEl: HTMLElement;
    roundNumber: number;
    courtNumber: number;
    pair: "pair1" | "pair2";
  } | null>(null);
  const [togglingHandicapId, setTogglingHandicapId] = useState<string | null>(null);
  const [scoreDialogMatch, setScoreDialogMatch] = useState<{
    roundNumber: number;
    courtNumber: number;
    pair1: { player1Id: string; player2Id: string };
    pair2: { player1Id: string; player2Id: string };
    isCompleted: boolean;
  } | null>(null);

  // Substitution dialog state
  const [subDialogOpen, setSubDialogOpen] = useState(false);
  const [subOriginalPlayerId, setSubOriginalPlayerId] = useState("");
  const [subMode, setSubMode] = useState<"existing" | "new">("existing");
  const [subSubstitute, setSubSubstitute] = useState<Player | null>(null);
  const [subNewName, setSubNewName] = useState("");
  const [subReason, setSubReason] = useState("");
  const [submittingSub, setSubmittingSub] = useState(false);

  const fetchTournament = useCallback(async () => {
    if (!tournamentId) return;
    try {
      const r = await apiRequest(`/api/tournaments/${tournamentId}`);
      const data = await r.json();
      setTournament(data);
    } catch (e) {
      console.error(e);
      setError("Failed to load tournament");
    } finally {
      setLoading(false);
    }
  }, [tournamentId]);

  const fetchPlayers = useCallback(async () => {
    try {
      const r = await apiRequest("/api/players");
      const data = await r.json();
      setPlayers(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    fetchTournament();
    fetchPlayers();
  }, [fetchTournament, fetchPlayers]);

  const playerName = useMemo(() => {
    const map = new Map(players.map(p => [p.id, p.name]));
    return (id: string) => map.get(id) || "(unknown player)";
  }, [players]);

  const roster = useMemo(
    () => players.filter(p => tournament?.americanaPlayerIds?.includes(p.id)),
    [players, tournament]
  );

  // Rounds/courts where the selected player still has an unscored match — these are what
  // a substitution would touch. Also collect who else is already playing in those rounds
  // so the "existing player" picker doesn't offer someone who'd get double-booked.
  const subAffectedRounds = useMemo(() => {
    if (!subOriginalPlayerId) return [] as { roundNumber: number; courtNumber: number }[];
    const rounds = tournament?.americanaRounds || [];
    const result: { roundNumber: number; courtNumber: number }[] = [];
    rounds.forEach(round => {
      const match = round.matches.find(m =>
        !m.isCompleted && [m.pair1.player1Id, m.pair1.player2Id, m.pair2.player1Id, m.pair2.player2Id].includes(subOriginalPlayerId)
      );
      if (match) result.push({ roundNumber: round.roundNumber, courtNumber: match.courtNumber });
    });
    return result.sort((a, b) => a.roundNumber - b.roundNumber);
  }, [subOriginalPlayerId, tournament?.americanaRounds]);

  const subBusyPlayerIds = useMemo(() => {
    if (!subOriginalPlayerId) return new Set<string>();
    const rounds = tournament?.americanaRounds || [];
    const affectedRoundNumbers = new Set(subAffectedRounds.map(r => r.roundNumber));
    const busy = new Set<string>();
    rounds.forEach(round => {
      if (!affectedRoundNumbers.has(round.roundNumber)) return;
      round.matches.forEach(m => {
        [m.pair1.player1Id, m.pair1.player2Id, m.pair2.player1Id, m.pair2.player2Id].forEach(id => {
          if (id !== subOriginalPlayerId) busy.add(id);
        });
      });
    });
    return busy;
  }, [subOriginalPlayerId, subAffectedRounds, tournament?.americanaRounds]);

  const openSubstituteDialog = () => {
    setSubOriginalPlayerId("");
    setSubMode("existing");
    setSubSubstitute(null);
    setSubNewName("");
    setSubReason("");
    setSubDialogOpen(true);
  };

  const submitSubstitution = async () => {
    if (!tournamentId || !subOriginalPlayerId) return;
    if (subMode === "existing" && !subSubstitute) return;
    if (subMode === "new" && !subNewName.trim()) return;

    setSubmittingSub(true);
    try {
      const r = await apiRequest(`/api/tournaments/${tournamentId}/substitute-americana-player`, {
        method: "POST",
        body: JSON.stringify({
          originalPlayerId: subOriginalPlayerId,
          ...(subMode === "existing" ? { substitutePlayerId: subSubstitute!.id } : { substituteName: subNewName.trim() }),
          ...(subReason.trim() ? { reason: subReason.trim() } : {}),
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || "Failed to substitute player");
      await Promise.all([fetchTournament(), fetchPlayers()]);
      setSubDialogOpen(false);
      setError(null);
    } catch (e: any) {
      console.error(e);
      setError(e.message || "Failed to substitute player");
    } finally {
      setSubmittingSub(false);
    }
  };

  useEffect(() => {
    if (!roster.length) {
      setStrengthOrderIds([]);
      return;
    }

    const rosterIds = roster.map(p => p.id);
    const rosterSet = new Set(rosterIds);
    const savedOrder = (tournament?.americanaStrengthOrder || []).filter(id => rosterSet.has(id));
    const remaining = roster
      .filter(p => !savedOrder.includes(p.id))
      .sort((a, b) => (b.level || 0) - (a.level || 0))
      .map(p => p.id);

    setStrengthOrderIds([...savedOrder, ...remaining]);
  }, [roster, tournament?.americanaStrengthOrder]);

  useEffect(() => {
    setHandicapCatalogDraft(tournament?.americanaHandicapTypes || []);
  }, [tournament?.americanaHandicapTypes]);

  const handicapTypeMap = useMemo(() => {
    const map = new Map<string, AmericanaHandicapType>();
    (tournament?.americanaHandicapTypes || []).forEach(h => map.set(h.id, h));
    return map;
  }, [tournament?.americanaHandicapTypes]);

  const effectiveStrengthOrder = useMemo(() => {
    const rosterIds = roster.map(p => p.id);
    const rosterSet = new Set(rosterIds);
    const ordered = strengthOrderIds.filter(id => rosterSet.has(id));
    const missing = rosterIds.filter(id => !ordered.includes(id));
    return [...ordered, ...missing];
  }, [strengthOrderIds, roster]);

  const strengthScoreMap = useMemo(() => {
    const map = new Map<string, number>();
    const total = effectiveStrengthOrder.length;
    effectiveStrengthOrder.forEach((playerId, index) => {
      map.set(playerId, total - index);
    });
    return map;
  }, [effectiveStrengthOrder]);

  const getMatchBalanceInfo = (match: AmericanaMatch): MatchBalanceInfo => {
    const teamA = (strengthScoreMap.get(match.pair1.player1Id) || 1) + (strengthScoreMap.get(match.pair1.player2Id) || 1);
    const teamB = (strengthScoreMap.get(match.pair2.player1Id) || 1) + (strengthScoreMap.get(match.pair2.player2Id) || 1);
    const diff = Math.abs(teamA - teamB);

    if (diff <= 1) {
      return { label: "Balanced", strongerTeam: null, diff, color: "success" };
    }
    if (diff <= 4) {
      return { label: "Stronger Side", strongerTeam: teamA > teamB ? "A" : "B", diff, color: "warning" };
    }
    return { label: "Much Stronger Side", strongerTeam: teamA > teamB ? "A" : "B", diff, color: "error" };
  };

  const moveStrengthPlayer = (index: number, direction: -1 | 1) => {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= effectiveStrengthOrder.length) return;
    setStrengthOrderIds(prev => {
      const ordered = [...prev];
      [ordered[index], ordered[nextIndex]] = [ordered[nextIndex], ordered[index]];
      return ordered;
    });
  };

  const saveStrengthOrder = async () => {
    if (!tournamentId) return;
    setSavingStrengthOrder(true);
    try {
      await apiRequest(`/api/tournaments/${tournamentId}`, {
        method: "PUT",
        body: JSON.stringify({ americanaStrengthOrder: effectiveStrengthOrder }),
      });
      await fetchTournament();
      setError(null);
    } catch (e) {
      console.error(e);
      setError("Failed to save strength order");
    } finally {
      setSavingStrengthOrder(false);
    }
  };

  const addHandicapTypeDraft = () => {
    const label = newHandicapLabel.trim();
    const emoji = newHandicapEmoji.trim();
    if (!label || !emoji) return;
    setHandicapCatalogDraft(prev => [...prev, { id: `hc-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, label, emoji }]);
    setNewHandicapLabel("");
    setNewHandicapEmoji("");
  };

  const removeHandicapTypeDraft = (id: string) => {
    setHandicapCatalogDraft(prev => prev.filter(h => h.id !== id));
  };

  const saveHandicapTypes = async () => {
    if (!tournamentId) return;
    setSavingHandicapTypes(true);
    try {
      const r = await apiRequest(`/api/tournaments/${tournamentId}/update-americana-handicap-types`, {
        method: "POST",
        body: JSON.stringify({ americanaHandicapTypes: handicapCatalogDraft }),
      });
      if (!r.ok) { const d = await r.json(); throw new Error(d.error); }
      await fetchTournament();
      setError(null);
    } catch (e: any) {
      console.error(e);
      setError(e.message || "Failed to save handicap types");
    } finally {
      setSavingHandicapTypes(false);
    }
  };

  const toggleMatchHandicap = async (roundNumber: number, courtNumber: number, pair: "pair1" | "pair2", handicapId: string) => {
    if (!tournamentId) return;
    setTogglingHandicapId(handicapId);
    try {
      const r = await apiRequest(`/api/tournaments/${tournamentId}/toggle-americana-match-handicap`, {
        method: "POST",
        body: JSON.stringify({ roundNumber, courtNumber, pair, handicapId }),
      });
      if (!r.ok) { const d = await r.json(); throw new Error(d.error); }
      await fetchTournament();
    } catch (e: any) {
      console.error(e);
      setError(e.message || "Failed to toggle handicap");
    } finally {
      setTogglingHandicapId(null);
    }
  };

  useEffect(() => {
    if (tournament?.americanaRounds && tournament.americanaRounds.length > 0) {
      setEditDraftRounds(draftRoundsFromSavedRounds(tournament.americanaRounds));
    } else {
      setEditDraftRounds([]);
      setIsEditingSchedule(false);
    }
  }, [tournament]);

  const buildAmericanaRoundsFromDraft = (
    sourceDraftRounds: DraftMatch[][],
    existingRounds?: AmericanaRound[]
  ): AmericanaRound[] => {
    return sourceDraftRounds.map((round, roundIndex) => ({
      roundNumber: roundIndex + 1,
      matches: round.map((m): AmericanaMatch => {
        const existingMatch = existingRounds
          ?.find(r => r.roundNumber === roundIndex + 1)
          ?.matches.find(existing => existing.courtNumber === m.courtNumber);

        const playersChanged = existingMatch
          ? (
            existingMatch.pair1.player1Id !== m.pair1Player1 ||
            existingMatch.pair1.player2Id !== m.pair1Player2 ||
            existingMatch.pair2.player1Id !== m.pair2Player1 ||
            existingMatch.pair2.player2Id !== m.pair2Player2
          )
          : true;

        const keepExistingScore = Boolean(existingMatch && !playersChanged && existingMatch.isCompleted);
        const score = keepExistingScore
          ? existingMatch!.score
          : { pair1Games: 0, pair2Games: 0, isCompleted: false, winnerPair: null as null };

        return {
          id: existingMatch?.id || `r${roundIndex + 1}-c${m.courtNumber}`,
          roundNumber: roundIndex + 1,
          courtNumber: m.courtNumber,
          pair1: { player1Id: m.pair1Player1, player2Id: m.pair1Player2 },
          pair2: { player1Id: m.pair2Player1, player2Id: m.pair2Player2 },
          score,
          isCompleted: keepExistingScore,
        };
      }),
    }));
  };

  const saveRoster = async () => {
    if (!tournamentId) return;
    setSaving(true);
    try {
      await apiRequest(`/api/tournaments/${tournamentId}`, {
        method: "PUT",
        body: JSON.stringify({ americanaPlayerIds: rosterDraft.map(p => p.id) }),
      });
      await fetchTournament();
    } catch (e) {
      console.error(e);
      setError("Failed to save player roster");
    } finally {
      setSaving(false);
    }
  };

  const startScheduleBuilder = () => {
    setCoverageAnalysis(null);
    setDraftRounds(emptyDraftRounds(roundCount, courtCount));
  };

  const generateTemplateDrawDraftRounds = (): DraftMatch[][] | null => {
    if (roster.length !== 16) {
      setError("The 5x4 Americana template requires exactly 16 players in the roster.");
      return null;
    }

    const randomizedPlayerIds = shuffle(roster.map(p => p.id));
    const numberToPlayerId = new Map<number, string>(
      randomizedPlayerIds.map((playerId, index) => [index + 1, playerId])
    );

    return AMERICANA_5X4_TEMPLATE.map(round =>
      round.map(match => ({
        courtNumber: match.courtNumber,
        pair1Player1: numberToPlayerId.get(match.pair1[0]) || "",
        pair1Player2: numberToPlayerId.get(match.pair1[1]) || "",
        pair2Player1: numberToPlayerId.get(match.pair2[0]) || "",
        pair2Player2: numberToPlayerId.get(match.pair2[1]) || "",
      }))
    );
  };

  const autoGenerateSchedule = () => {
    if (roundCount !== 5 || courtCount !== 4) {
      setError("Auto-generation from the Americana template is available for exactly 5 rounds and 4 courts.");
      return;
    }

    const generatedDraftRounds = generateTemplateDrawDraftRounds();
    if (!generatedDraftRounds) return;

    setDraftRounds(generatedDraftRounds);
    setCoverageAnalysis(null);
    setError(null);
  };

  const regenerateDraftSchedule = () => {
    if (roundCount !== 5 || courtCount !== 4) {
      setError("Regenerate draw is available for the 5-round, 4-court Americana template.");
      return;
    }
    const generatedDraftRounds = generateTemplateDrawDraftRounds();
    if (!generatedDraftRounds) return;
    setDraftRounds(generatedDraftRounds);
    setCoverageAnalysis(null);
    setError(null);
  };

  const regenerateSavedSchedule = () => {
    const roundCountFromSchedule = tournament?.americanaRounds?.length || editDraftRounds.length;
    const courtCountFromSchedule = editDraftRounds[0]?.length || tournament?.americanaRounds?.[0]?.matches?.length || 0;

    if (roundCountFromSchedule !== 5 || courtCountFromSchedule !== 4) {
      setError("Regenerate draw is available for the 5-round, 4-court Americana template.");
      return;
    }

    const generatedDraftRounds = generateTemplateDrawDraftRounds();
    if (!generatedDraftRounds) return;

    setEditDraftRounds(generatedDraftRounds);
    setIsEditingSchedule(true);
    setError(null);
  };

  const updateDraftMatch = (roundIndex: number, courtIndex: number, field: keyof DraftMatch, value: string) => {
    setDraftRounds(prev => {
      const next = prev.map(round => round.map(match => ({ ...match })));
      (next[roundIndex][courtIndex] as any)[field] = value;
      return next;
    });
    setCoverageAnalysis(null);
  };

  const runCoverageAnalysis = () => {
    if (!draftRounds.length) {
      setError("Start or generate a schedule first, then run analysis.");
      return;
    }
    const analysis = analyzeMeetingCoverage(draftRounds, roster.map(p => p.id));
    setCoverageAnalysis(analysis);
    setError(null);
  };

  const saveSchedule = async () => {
    if (!tournamentId) return;
    const allFilled = draftRounds.every(round =>
      round.every(m => m.pair1Player1 && m.pair1Player2 && m.pair2Player1 && m.pair2Player2)
    );
    if (!allFilled) {
      setError("Every court in every round needs 4 players assigned before saving.");
      return;
    }

    const americanaRounds = buildAmericanaRoundsFromDraft(draftRounds);

    setSaving(true);
    try {
      await apiRequest(`/api/tournaments/${tournamentId}/update-americana-schedule`, {
        method: "POST",
        body: JSON.stringify({ americanaRounds }),
      });
      await fetchTournament();
      setDraftRounds([]);
      setError(null);
    } catch (e) {
      console.error(e);
      setError("Failed to save schedule");
    } finally {
      setSaving(false);
    }
  };

  const updateEditDraftMatch = (roundIndex: number, courtIndex: number, field: keyof DraftMatch, value: string) => {
    setEditDraftRounds(prev => {
      const next = prev.map(round => round.map(match => ({ ...match })));
      (next[roundIndex][courtIndex] as DraftMatch)[field] = value;
      return next;
    });
  };

  const saveEditedSchedule = async () => {
    if (!tournamentId || !tournament?.americanaRounds) return;

    const allFilled = editDraftRounds.every(round =>
      round.every(m => m.pair1Player1 && m.pair1Player2 && m.pair2Player1 && m.pair2Player2)
    );

    if (!allFilled) {
      setError("Every court in every round needs 4 players assigned before saving.");
      return;
    }

    const americanaRounds = buildAmericanaRoundsFromDraft(editDraftRounds, tournament.americanaRounds);

    setSaving(true);
    try {
      await apiRequest(`/api/tournaments/${tournamentId}/update-americana-schedule`, {
        method: "POST",
        body: JSON.stringify({ americanaRounds }),
      });
      await fetchTournament();
      setIsEditingSchedule(false);
      setError(null);
    } catch (e) {
      console.error(e);
      setError("Failed to update schedule");
    } finally {
      setSaving(false);
    }
  };

  const scoreKey = (roundNumber: number, courtNumber: number) => `${roundNumber}-${courtNumber}`;

  const saveScore = async (roundNumber: number, courtNumber: number) => {
    if (!tournamentId) return;
    const key = scoreKey(roundNumber, courtNumber);
    const draft = scoreDrafts[key];
    if (!draft || draft.pair1Games === "" || draft.pair2Games === "") return;

    setSaving(true);
    try {
      await apiRequest(`/api/tournaments/${tournamentId}/score-americana-match`, {
        method: "POST",
        body: JSON.stringify({
          roundNumber,
          courtNumber,
          pair1Games: Number(draft.pair1Games),
          pair2Games: Number(draft.pair2Games),
        }),
      });
      await fetchTournament();
      setError(null);
    } catch (e) {
      console.error(e);
      setError("Failed to save score");
    } finally {
      setSaving(false);
    }
  };

  const openScoreDialog = (roundNumber: number, match: AmericanaMatch) => {
    const key = scoreKey(roundNumber, match.courtNumber);
    if (!scoreDrafts[key]) {
      setScoreDrafts(prev => ({
        ...prev,
        [key]: {
          pair1Games: match.isCompleted ? String(match.score.pair1Games) : "",
          pair2Games: match.isCompleted ? String(match.score.pair2Games) : "",
        },
      }));
    }

    setScoreDialogMatch({
      roundNumber,
      courtNumber: match.courtNumber,
      pair1: match.pair1,
      pair2: match.pair2,
      isCompleted: match.isCompleted,
    });
  };

  const completeTournament = async () => {
    if (!tournamentId) return;
    setSaving(true);
    try {
      await apiRequest(`/api/tournaments/${tournamentId}/complete-americana`, { method: "POST" });
      await fetchTournament();
      setError(null);
    } catch (e) {
      console.error(e);
      setError("Failed to complete tournament — make sure every match has a score.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!tournament) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Tournament not found.</Alert>
      </Box>
    );
  }

  const allMatches = (tournament.americanaRounds || []).flatMap(r => r.matches);
  const allScored = allMatches.length > 0 && allMatches.every(m => m.isCompleted);
  const sortedAmericanaRounds = (tournament.americanaRounds || [])
    .slice()
    .sort((a, b) => a.roundNumber - b.roundNumber)
    .map(round => ({
      ...round,
      matches: round.matches.slice().sort((a, b) => a.courtNumber - b.courtNumber),
    }));
  const maxCourts = sortedAmericanaRounds.reduce((max, round) => Math.max(max, round.matches.length), 0);
  const matchDuration = tournament.timedMatchDuration || 20;
  const changeover = tournament.changeoverTime || 0;
  const startMinutes = parseTimeToMinutes(tournament.startTime);
  const eventRows = ((tournament.events || []) as any[])
    .map((event, index) => {
      const start = parseAnyTimeToMinutes(event.startTime);
      if (start === null) return null;
      const duration = Number(event.duration) || 0;
      return {
        id: event.id || `event-${index}`,
        name: event.name || "Event",
        description: event.description || "",
        eventType: event.type || "other",
        start,
        end: start + duration,
        duration,
      };
    })
    .filter((e): e is { id: string; name: string; description: string; eventType: string; start: number; end: number; duration: number } => Boolean(e))
    .sort((a, b) => a.start - b.start);
  const scheduleRows: Array<
    | { kind: "round"; round: AmericanaRound; start: number; end: number }
    | { kind: "event"; id: string; name: string; description: string; eventType: string; start: number; end: number; duration: number }
  > = [
    ...sortedAmericanaRounds.map((round, roundIndex) => {
      const start = startMinutes + roundIndex * (matchDuration + changeover);
      return {
        kind: "round" as const,
        round,
        start,
        end: start + matchDuration,
      };
    }),
    ...eventRows.map(event => ({
      kind: "event" as const,
      ...event,
    })),
  ].sort((a, b) => a.start - b.start || (a.kind === "event" ? -1 : 1));

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton onClick={() => navigate("/admin/tournaments")}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" fontWeight={700}>{tournament.name}</Typography>
        <Chip label="Americana" size="small" color="primary" />
        <Chip
          label={tournament.status === "completed" ? "Completed" : "In Progress"}
          size="small"
          color={tournament.status === "completed" ? "success" : "default"}
        />
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Step 1: roster */}
      {(!tournament.americanaPlayerIds || tournament.americanaPlayerIds.length === 0) && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Select Players</Typography>
            <Autocomplete
              multiple
              options={players}
              getOptionLabel={(p) => p.name}
              value={rosterDraft}
              onChange={(_, value) => setRosterDraft(value)}
              renderInput={(params) => <TextField {...params} label="Players in this event" placeholder="Add player" />}
            />
            <Button
              variant="contained"
              sx={{ mt: 2 }}
              disabled={rosterDraft.length < 4 || saving}
              onClick={saveRoster}
            >
              Save Player List ({rosterDraft.length})
            </Button>
          </CardContent>
        </Card>
      )}

      {tournament.americanaPlayerIds && tournament.americanaPlayerIds.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Accordion disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} sx={{ width: "100%", pr: 1 }}>
                  <Typography variant="h6">Player Strength Order</Typography>
                  <Chip size="small" label={`${effectiveStrengthOrder.length} players`} variant="outlined" />
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} sx={{ mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Sort strongest to weakest. This order is used to flag matches as balanced, stronger side, or much stronger side.
                  </Typography>
                  <Button variant="contained" disabled={savingStrengthOrder} onClick={saveStrengthOrder}>
                    Save Strength Order
                  </Button>
                </Stack>

                <Grid container spacing={1}>
                  {effectiveStrengthOrder.map((playerId, index) => (
                    <Grid item xs={12} sm={6} md={4} key={`strength-${playerId}`}>
                      <Paper variant="outlined" sx={{ p: 1, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">#{index + 1}</Typography>
                          <Typography variant="body2" fontWeight={600}>{playerName(playerId)}</Typography>
                        </Box>
                        <Stack direction="row" spacing={0.5}>
                          <IconButton size="small" onClick={() => moveStrengthPlayer(index, -1)} disabled={index === 0}>
                            <ArrowUpwardIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => moveStrengthPlayer(index, 1)} disabled={index === effectiveStrengthOrder.length - 1}>
                            <ArrowDownwardIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </AccordionDetails>
            </Accordion>
          </CardContent>
        </Card>
      )}

      {tournament.americanaPlayerIds && tournament.americanaPlayerIds.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Accordion disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} sx={{ width: "100%", pr: 1 }}>
                  <Typography variant="h6">Handicaps</Typography>
                  <Chip size="small" label={`${handicapCatalogDraft.length} types`} variant="outlined" />
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Define reusable handicap types here, then assign them to a team on any match in the schedule below.
                  Assigned handicaps show as emoji badges next to that team for that match.
                </Typography>

                <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1, mb: 2 }}>
                  {handicapCatalogDraft.map(h => (
                    <Chip
                      key={h.id}
                      label={`${h.emoji} ${h.label}`}
                      onDelete={() => removeHandicapTypeDraft(h.id)}
                      variant="outlined"
                    />
                  ))}
                  {handicapCatalogDraft.length === 0 && (
                    <Typography variant="body2" color="text.secondary">No handicap types yet — add one below.</Typography>
                  )}
                </Stack>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }}>
                  <TextField
                    label="Emoji"
                    value={newHandicapEmoji}
                    onChange={e => setNewHandicapEmoji(e.target.value)}
                    sx={{ width: { xs: "100%", sm: 90 } }}
                    size="small"
                    inputProps={{ maxLength: 4 }}
                  />
                  <TextField
                    label="Handicap name"
                    value={newHandicapLabel}
                    onChange={e => setNewHandicapLabel(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") addHandicapTypeDraft(); }}
                    sx={{ flex: 1 }}
                    size="small"
                    placeholder='e.g. "Spot 2 games"'
                  />
                  <Button variant="outlined" startIcon={<AddIcon />} onClick={addHandicapTypeDraft} disabled={!newHandicapLabel.trim() || !newHandicapEmoji.trim()}>
                    Add
                  </Button>
                  <Button variant="contained" onClick={saveHandicapTypes} disabled={savingHandicapTypes}>
                    {savingHandicapTypes ? "Saving…" : "Save"}
                  </Button>
                </Stack>
              </AccordionDetails>
            </Accordion>
          </CardContent>
        </Card>
      )}

      {/* Step 2: schedule builder */}
      {tournament.americanaPlayerIds && tournament.americanaPlayerIds.length > 0 &&
        (!tournament.americanaRounds || tournament.americanaRounds.length === 0) && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} sx={{ mb: 1 }}>
              <Typography variant="h6">Build Round Schedule</Typography>
              <Button variant="outlined" onClick={runCoverageAnalysis} disabled={draftRounds.length === 0}>
                Analyze Coverage
              </Button>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {roster.length} players selected. Enter the fixed pairing for each round/court exactly as planned.
            </Typography>

            {coverageAnalysis && (
              <Alert
                severity={coverageAnalysis.missingByPlayer.length === 0 && coverageAnalysis.unassignedSlots === 0 ? "success" : "warning"}
                sx={{ mb: 2 }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {coverageAnalysis.playersMeetingEveryone}/{coverageAnalysis.totalPlayers} players meet all other participants at least once.
                </Typography>
                {coverageAnalysis.unassignedSlots > 0 && (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {coverageAnalysis.unassignedSlots} player slot(s) are still unassigned, so coverage is incomplete.
                  </Typography>
                )}
                {coverageAnalysis.missingByPlayer.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    {coverageAnalysis.missingByPlayer.map(item => (
                      <Typography key={item.playerId} variant="body2">
                        {playerName(item.playerId)} is still missing: {item.missingPlayerIds.map(id => playerName(id)).join(", ")}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Alert>
            )}

            {draftRounds.length === 0 ? (
              <Stack direction="row" spacing={2} alignItems="center">
                <TextField
                  label="Rounds"
                  type="number"
                  size="small"
                  value={roundCount}
                  onChange={(e) => setRoundCount(Number(e.target.value) || 1)}
                  InputProps={{ inputProps: { min: 1, max: 10 } }}
                />
                <TextField
                  label="Courts"
                  type="number"
                  size="small"
                  value={courtCount}
                  onChange={(e) => setCourtCount(Number(e.target.value) || 1)}
                  InputProps={{ inputProps: { min: 1, max: 8 } }}
                />
                <Button variant="contained" onClick={startScheduleBuilder}>
                  Start Empty Schedule
                </Button>
                <Button variant="outlined" onClick={autoGenerateSchedule}>
                  Auto-Generate 5x4 Template
                </Button>
              </Stack>
            ) : (
              <>
                {draftRounds.map((round, roundIndex) => (
                  <Box key={roundIndex} sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                      Round {roundIndex + 1}
                    </Typography>
                    <Grid container spacing={2}>
                      {round.map((match, courtIndex) => (
                        <Grid item xs={12} sm={6} md={3} key={courtIndex}>
                          <Paper variant="outlined" sx={{ p: 1.5 }}>
                            <Typography variant="caption" color="text.secondary">Court {match.courtNumber}</Typography>
                            {([
                              ["pair1Player1", "Pair 1 - Player A"],
                              ["pair1Player2", "Pair 1 - Player B"],
                              ["pair2Player1", "Pair 2 - Player A"],
                              ["pair2Player2", "Pair 2 - Player B"],
                            ] as [Exclude<keyof DraftMatch, "courtNumber">, string][]).map(([field, label]) => (
                              <FormControl fullWidth size="small" key={field} sx={{ mt: 1 }}>
                                <InputLabel>{label}</InputLabel>
                                <Select
                                  value={match[field]}
                                  label={label}
                                  onChange={(e) => updateDraftMatch(roundIndex, courtIndex, field, e.target.value)}
                                >
                                  {roster.map(p => (
                                    <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            ))}
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                ))}
                <Button variant="contained" disabled={saving} onClick={saveSchedule}>
                  Save Schedule
                </Button>
                <Button variant="outlined" sx={{ ml: 1 }} onClick={regenerateDraftSchedule}>
                  Regenerate Draw
                </Button>
                <Button sx={{ ml: 1 }} startIcon={<DeleteIcon />} onClick={() => { setDraftRounds([]); setCoverageAnalysis(null); }}>
                  Discard
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 2b: schedule editing after generation */}
      {tournament.americanaRounds && tournament.americanaRounds.length > 0 && tournament.status !== "completed" && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }} justifyContent="space-between" sx={{ mb: 2 }}>
              <Typography variant="h6">Edit Schedule</Typography>
              {!isEditingSchedule ? (
                <Stack direction="row" spacing={1}>
                  <Button variant="outlined" onClick={regenerateSavedSchedule}>
                    Regenerate Draw
                  </Button>
                  <Button variant="outlined" onClick={() => setIsEditingSchedule(true)}>
                    Edit Matches
                  </Button>
                </Stack>
              ) : (
                <Stack direction="row" spacing={1}>
                  <Button variant="outlined" onClick={regenerateSavedSchedule}>
                    Regenerate Draw
                  </Button>
                  <Button variant="contained" disabled={saving} onClick={saveEditedSchedule}>
                    Save Changes
                  </Button>
                  <Button
                    onClick={() => {
                      setEditDraftRounds(draftRoundsFromSavedRounds(tournament.americanaRounds || []));
                      setIsEditingSchedule(false);
                    }}
                  >
                    Cancel
                  </Button>
                </Stack>
              )}
            </Stack>

            {isEditingSchedule && (
              <>
                {editDraftRounds.map((round, roundIndex) => (
                  <Box key={roundIndex} sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                      Round {roundIndex + 1}
                    </Typography>
                    <Grid container spacing={2}>
                      {round.map((match, courtIndex) => (
                        <Grid item xs={12} sm={6} md={3} key={`edit-${roundIndex}-${courtIndex}`}>
                          <Paper variant="outlined" sx={{ p: 1.5 }}>
                            <Typography variant="caption" color="text.secondary">Court {match.courtNumber}</Typography>
                            {([
                              ["pair1Player1", "Pair 1 - Player A"],
                              ["pair1Player2", "Pair 1 - Player B"],
                              ["pair2Player1", "Pair 2 - Player A"],
                              ["pair2Player2", "Pair 2 - Player B"],
                            ] as [Exclude<keyof DraftMatch, "courtNumber">, string][]).map(([field, label]) => (
                              <FormControl fullWidth size="small" key={`${field}-${roundIndex}-${courtIndex}`} sx={{ mt: 1 }}>
                                <InputLabel>{label}</InputLabel>
                                <Select
                                  value={match[field]}
                                  label={label}
                                  onChange={(e) => updateEditDraftMatch(roundIndex, courtIndex, field, e.target.value)}
                                >
                                  {roster.map(p => (
                                    <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            ))}
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                ))}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 3: scoring */}
      {sortedAmericanaRounds.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="h6">Schedule & Scoring</Typography>
              <Stack direction="row" spacing={1}>
                <Button size="small" variant="outlined" onClick={() => window.open(`/americana-display/${tournamentId}`, "_blank")}>
                  📺 Live Display
                </Button>
                {tournament.status !== "completed" && (
                  <Button size="small" variant="outlined" onClick={openSubstituteDialog}>
                    Substitute Player
                  </Button>
                )}
              </Stack>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Times are generated from tournament start time and match duration.
            </Typography>

            {tournament.americanaSubstitutions && tournament.americanaSubstitutions.length > 0 && (
              <Stack spacing={0.5} sx={{ mb: 2 }}>
                {tournament.americanaSubstitutions.map(sub => (
                  <Typography key={sub.id} variant="caption" color="text.secondary">
                    {sub.originalPlayerName} → {sub.substitutePlayerName}
                    {sub.isNewGuestPlayer ? " (guest)" : ""} from round {Math.min(...sub.roundsAffected)}
                    {sub.reason ? ` — ${sub.reason}` : ""}
                  </Typography>
                ))}
              </Stack>
            )}

            <Box sx={{ overflowX: "auto", border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
              <table style={{ width: "100%", minWidth: 940, borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th
                      style={{
                        textAlign: "left",
                        padding: 10,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        borderRight: `1px solid ${theme.palette.divider}`,
                        backgroundColor: theme.palette.mode === "dark" ? alpha(theme.palette.common.white, 0.06) : theme.palette.grey[100],
                        width: 200,
                      }}
                    >
                      Time / Slot
                    </th>
                    {Array.from({ length: Math.max(maxCourts, 1) }, (_, i) => (
                      <th
                        key={`court-head-${i + 1}`}
                        style={{
                          textAlign: "left",
                          padding: 10,
                          borderBottom: `1px solid ${theme.palette.divider}`,
                          borderRight: i < Math.max(maxCourts, 1) - 1 ? `1px solid ${theme.palette.divider}` : "none",
                          backgroundColor: theme.palette.mode === "dark" ? alpha(theme.palette.common.white, 0.06) : theme.palette.grey[100],
                        }}
                      >
                        Court {i + 1}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {scheduleRows.map((row, idx) => {
                    const timeLabel = `${minutesToTimeLabel(row.start)} - ${minutesToTimeLabel(row.end)}`;

                    if (row.kind === "event") {
                      return (
                        <tr key={`event-row-${row.id}-${idx}`}>
                          <td
                            style={{
                              padding: 10,
                              borderTop: `1px solid ${theme.palette.divider}`,
                              borderRight: `1px solid ${theme.palette.divider}`,
                              backgroundColor: theme.palette.mode === "dark" ? alpha(theme.palette.info.main, 0.12) : alpha(theme.palette.info.light, 0.3),
                              verticalAlign: "top",
                            }}
                          >
                            <Typography variant="subtitle2" fontWeight={700}>Event</Typography>
                            <Typography variant="caption" color="text.secondary">{timeLabel}</Typography>
                          </td>
                          <td
                            colSpan={Math.max(maxCourts, 1)}
                            style={{
                              padding: 10,
                              borderTop: `1px solid ${theme.palette.divider}`,
                              backgroundColor: theme.palette.mode === "dark" ? alpha(theme.palette.info.main, 0.12) : alpha(theme.palette.info.light, 0.18),
                            }}
                          >
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                              <Chip label={row.eventType || "event"} size="small" color="info" variant="outlined" />
                              <Typography variant="subtitle2" fontWeight={700}>{row.name}</Typography>
                              {row.duration > 0 && (
                                <Typography variant="caption" color="text.secondary">({row.duration} min)</Typography>
                              )}
                            </Stack>
                            {row.description && (
                              <Typography variant="body2" color="text.secondary">{row.description}</Typography>
                            )}
                          </td>
                        </tr>
                      );
                    }

                    const round = row.round;

                    return (
                      <tr key={`round-row-${round.roundNumber}`}>
                        <td
                          style={{
                            padding: 10,
                            borderTop: `1px solid ${theme.palette.divider}`,
                            borderRight: `1px solid ${theme.palette.divider}`,
                            backgroundColor: theme.palette.mode === "dark" ? alpha(theme.palette.common.white, 0.03) : theme.palette.grey[50],
                            verticalAlign: "top",
                          }}
                        >
                          <Typography variant="subtitle2" fontWeight={700}>Round {round.roundNumber}</Typography>
                          <Typography variant="caption" color="text.secondary">{timeLabel}</Typography>
                        </td>

                        {Array.from({ length: Math.max(maxCourts, 1) }, (_, idx2) => {
                          const courtNumber = idx2 + 1;
                          const match = round.matches.find(m => m.courtNumber === courtNumber);

                          if (!match) {
                            return (
                              <td
                                key={`empty-${round.roundNumber}-${courtNumber}`}
                                style={{
                                  padding: 10,
                                  borderTop: `1px solid ${theme.palette.divider}`,
                                  borderRight: courtNumber < Math.max(maxCourts, 1) ? `1px solid ${theme.palette.divider}` : "none",
                                  backgroundColor: theme.palette.mode === "dark" ? alpha(theme.palette.common.white, 0.02) : theme.palette.grey[50],
                                }}
                              />
                            );
                          }

                          const balance = getMatchBalanceInfo(match);
                          const balanceLabel = balance.strongerTeam
                            ? `${balance.label} (Team ${balance.strongerTeam})`
                            : balance.label;

                          return (
                            <td
                              key={match.id}
                              style={{
                                padding: 10,
                                borderTop: `1px solid ${theme.palette.divider}`,
                                borderRight: courtNumber < Math.max(maxCourts, 1) ? `1px solid ${theme.palette.divider}` : "none",
                                verticalAlign: "top",
                                cursor: "pointer",
                              }}
                              onClick={() => openScoreDialog(round.roundNumber, match)}
                            >
                              <Stack spacing={0.75} sx={{ mb: 1 }}>
                                <Box
                                  sx={{
                                    p: 1,
                                    borderRadius: 1,
                                    bgcolor: theme.palette.mode === "dark"
                                      ? alpha(theme.palette.primary.main, 0.12)
                                      : alpha(theme.palette.primary.light, 0.16),
                                  }}
                                >
                                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                      <Typography variant="caption" color="primary" fontWeight={700}>Team A</Typography>
                                      {(match.pair1HandicapIds || []).map(id => handicapTypeMap.get(id)).filter(Boolean).map(h => (
                                        <Tooltip key={h!.id} title={h!.label}>
                                          <span style={{ fontSize: 14, lineHeight: 1 }}>{h!.emoji}</span>
                                        </Tooltip>
                                      ))}
                                    </Stack>
                                    {handicapCatalogDraft.length > 0 && (
                                      <IconButton
                                        size="small"
                                        sx={{ p: 0.25 }}
                                        onClick={e => { e.stopPropagation(); setHandicapMenu({ anchorEl: e.currentTarget, roundNumber: round.roundNumber, courtNumber: match.courtNumber, pair: "pair1" }); }}
                                      >
                                        <HandicapIcon sx={{ fontSize: 14 }} />
                                      </IconButton>
                                    )}
                                  </Stack>
                                  <Typography variant="body2" fontWeight={600}>
                                    {playerName(match.pair1.player1Id)} / {playerName(match.pair1.player2Id)}
                                  </Typography>
                                </Box>

                                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textAlign: "center" }}>
                                  vs
                                </Typography>

                                <Box
                                  sx={{
                                    p: 1,
                                    borderRadius: 1,
                                    bgcolor: theme.palette.mode === "dark"
                                      ? alpha(theme.palette.secondary.main, 0.12)
                                      : alpha(theme.palette.secondary.light, 0.18),
                                  }}
                                >
                                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                      <Typography variant="caption" color="secondary" fontWeight={700}>Team B</Typography>
                                      {(match.pair2HandicapIds || []).map(id => handicapTypeMap.get(id)).filter(Boolean).map(h => (
                                        <Tooltip key={h!.id} title={h!.label}>
                                          <span style={{ fontSize: 14, lineHeight: 1 }}>{h!.emoji}</span>
                                        </Tooltip>
                                      ))}
                                    </Stack>
                                    {handicapCatalogDraft.length > 0 && (
                                      <IconButton
                                        size="small"
                                        sx={{ p: 0.25 }}
                                        onClick={e => { e.stopPropagation(); setHandicapMenu({ anchorEl: e.currentTarget, roundNumber: round.roundNumber, courtNumber: match.courtNumber, pair: "pair2" }); }}
                                      >
                                        <HandicapIcon sx={{ fontSize: 14 }} />
                                      </IconButton>
                                    )}
                                  </Stack>
                                  <Typography variant="body2" fontWeight={600}>
                                    {playerName(match.pair2.player1Id)} / {playerName(match.pair2.player2Id)}
                                  </Typography>
                                </Box>
                              </Stack>

                              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: "wrap" }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Click match to enter score
                                  </Typography>
                                  <Chip label={balanceLabel} size="small" color={balance.color} variant="outlined" />
                                </Stack>
                                {match.isCompleted && <Chip label="Scored" size="small" color="success" sx={{ ml: 1 }} />}
                              </Stack>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Box>
          </CardContent>
        </Card>
      )}

      <Menu
        anchorEl={handicapMenu?.anchorEl || null}
        open={Boolean(handicapMenu)}
        onClose={() => setHandicapMenu(null)}
      >
        {handicapCatalogDraft.length === 0 && (
          <MenuItem disabled>No handicap types defined yet</MenuItem>
        )}
        {handicapCatalogDraft.map(h => {
          const menuRound = sortedAmericanaRounds.find(r => r.roundNumber === handicapMenu?.roundNumber);
          const menuMatch = menuRound?.matches.find(m => m.courtNumber === handicapMenu?.courtNumber);
          const activeIds = handicapMenu?.pair === "pair1" ? menuMatch?.pair1HandicapIds : menuMatch?.pair2HandicapIds;
          const active = !!activeIds?.includes(h.id);
          return (
            <MenuItem
              key={h.id}
              selected={active}
              disabled={togglingHandicapId === h.id}
              onClick={() => handicapMenu && toggleMatchHandicap(handicapMenu.roundNumber, handicapMenu.courtNumber, handicapMenu.pair, h.id)}
            >
              <Box component="span" sx={{ mr: 1 }}>{h.emoji}</Box> {h.label} {active ? "✓" : ""}
            </MenuItem>
          );
        })}
      </Menu>

      {/* Step 4: standings */}
      {tournament.americanaStandings && tournament.americanaStandings.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Standings</Typography>
            <Box sx={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    {["#", "Player", "MP", "W", "D", "L", "GD", "Pts"].map(h => (
                      <th key={h} style={{ textAlign: "left", padding: 6, borderBottom: "1px solid #ddd" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tournament.americanaStandings.map((s: AmericanaStanding, i) => (
                    <tr key={s.playerId}>
                      <td style={{ padding: 6 }}>{s.position ?? i + 1}</td>
                      <td style={{ padding: 6 }}>{playerName(s.playerId)}</td>
                      <td style={{ padding: 6 }}>{s.matchesPlayed}</td>
                      <td style={{ padding: 6 }}>{s.wins}</td>
                      <td style={{ padding: 6 }}>{s.draws}</td>
                      <td style={{ padding: 6 }}>{s.losses}</td>
                      <td style={{ padding: 6 }}>{s.gamesDiff}</td>
                      <td style={{ padding: 6 }}>{s.points}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          </CardContent>
        </Card>
      )}

      {tournament.status !== "completed" && (
        <Button variant="contained" color="success" disabled={!allScored || saving} onClick={completeTournament}>
          Complete Tournament
        </Button>
      )}

      <Dialog
        open={Boolean(scoreDialogMatch)}
        onClose={() => setScoreDialogMatch(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Enter Match Score</DialogTitle>
        {scoreDialogMatch && (
          <>
            <DialogContent>
              {(() => {
                const balance = getMatchBalanceInfo({
                  pair1: scoreDialogMatch.pair1,
                  pair2: scoreDialogMatch.pair2,
                } as AmericanaMatch);
                const balanceLabel = balance.strongerTeam
                  ? `${balance.label} (Team ${balance.strongerTeam})`
                  : balance.label;
                return (
                  <Chip
                    label={balanceLabel}
                    size="small"
                    color={balance.color}
                    variant="outlined"
                    sx={{ mb: 1 }}
                  />
                );
              })()}

              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Round {scoreDialogMatch.roundNumber} • Court {scoreDialogMatch.courtNumber}
              </Typography>

              <Box sx={{ p: 1, borderRadius: 1, bgcolor: theme.palette.mode === "dark" ? alpha(theme.palette.primary.main, 0.12) : alpha(theme.palette.primary.light, 0.16), mb: 1 }}>
                <Typography variant="caption" color="primary" fontWeight={700}>Team A</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {playerName(scoreDialogMatch.pair1.player1Id)} / {playerName(scoreDialogMatch.pair1.player2Id)}
                </Typography>
              </Box>

              <Box sx={{ p: 1, borderRadius: 1, bgcolor: theme.palette.mode === "dark" ? alpha(theme.palette.secondary.main, 0.12) : alpha(theme.palette.secondary.light, 0.18), mb: 2 }}>
                <Typography variant="caption" color="secondary" fontWeight={700}>Team B</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {playerName(scoreDialogMatch.pair2.player1Id)} / {playerName(scoreDialogMatch.pair2.player2Id)}
                </Typography>
              </Box>

              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  size="small"
                  type="number"
                  label="Team A"
                  value={scoreDrafts[scoreKey(scoreDialogMatch.roundNumber, scoreDialogMatch.courtNumber)]?.pair1Games || ""}
                  sx={{ width: 120 }}
                  onChange={(e) => {
                    const key = scoreKey(scoreDialogMatch.roundNumber, scoreDialogMatch.courtNumber);
                    const current = scoreDrafts[key] || { pair1Games: "", pair2Games: "" };
                    setScoreDrafts(prev => ({ ...prev, [key]: { ...current, pair1Games: e.target.value } }));
                  }}
                />
                <Typography variant="body2" color="text.secondary">vs</Typography>
                <TextField
                  size="small"
                  type="number"
                  label="Team B"
                  value={scoreDrafts[scoreKey(scoreDialogMatch.roundNumber, scoreDialogMatch.courtNumber)]?.pair2Games || ""}
                  sx={{ width: 120 }}
                  onChange={(e) => {
                    const key = scoreKey(scoreDialogMatch.roundNumber, scoreDialogMatch.courtNumber);
                    const current = scoreDrafts[key] || { pair1Games: "", pair2Games: "" };
                    setScoreDrafts(prev => ({ ...prev, [key]: { ...current, pair2Games: e.target.value } }));
                  }}
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setScoreDialogMatch(null)}>Cancel</Button>
              <Button
                variant="contained"
                disabled={saving}
                onClick={async () => {
                  await saveScore(scoreDialogMatch.roundNumber, scoreDialogMatch.courtNumber);
                  setScoreDialogMatch(null);
                }}
              >
                {scoreDialogMatch.isCompleted ? "Update" : "Save"}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog
        open={subDialogOpen}
        onClose={() => !submittingSub && setSubDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Substitute Player</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl size="small" fullWidth>
              <InputLabel id="sub-original-player-label">Player leaving</InputLabel>
              <Select
                labelId="sub-original-player-label"
                label="Player leaving"
                value={subOriginalPlayerId}
                onChange={(e) => {
                  setSubOriginalPlayerId(e.target.value as string);
                  setSubSubstitute(null);
                }}
              >
                {roster.map(p => (
                  <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {subOriginalPlayerId && (
              subAffectedRounds.length > 0 ? (
                <Alert severity="info">
                  This will replace them in round{subAffectedRounds.length > 1 ? "s" : ""}{" "}
                  {subAffectedRounds.map(r => r.roundNumber).join(", ")}. Already-scored matches are untouched.
                </Alert>
              ) : (
                <Alert severity="warning">This player has no remaining unscored matches to substitute.</Alert>
              )
            )}

            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant={subMode === "existing" ? "contained" : "outlined"}
                onClick={() => setSubMode("existing")}
              >
                Existing player
              </Button>
              <Button
                size="small"
                variant={subMode === "new" ? "contained" : "outlined"}
                onClick={() => setSubMode("new")}
              >
                Not in the system
              </Button>
            </Stack>

            {subMode === "existing" ? (
              <Autocomplete
                size="small"
                options={players.filter(p => p.id !== subOriginalPlayerId && !subBusyPlayerIds.has(p.id))}
                getOptionLabel={(p) => p.name}
                value={subSubstitute}
                onChange={(_e, value) => setSubSubstitute(value)}
                renderInput={(params) => <TextField {...params} label="Substitute player" />}
              />
            ) : (
              <TextField
                size="small"
                label="Substitute's name"
                value={subNewName}
                onChange={(e) => setSubNewName(e.target.value)}
                fullWidth
              />
            )}

            <TextField
              size="small"
              label="Reason (optional)"
              value={subReason}
              onChange={(e) => setSubReason(e.target.value)}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubDialogOpen(false)} disabled={submittingSub}>Cancel</Button>
          <Button
            variant="contained"
            onClick={submitSubstitution}
            disabled={
              submittingSub ||
              !subOriginalPlayerId ||
              subAffectedRounds.length === 0 ||
              (subMode === "existing" ? !subSubstitute : !subNewName.trim())
            }
          >
            Substitute
          </Button>
        </DialogActions>
      </Dialog>

      <Divider sx={{ my: 3 }} />
      <Typography variant="caption" color="text.secondary">
        This event doesn't affect league division standings or finances — it's tracked
        separately{tournament.isPointsRelevant === false ? " and doesn't award ranking points" : ""}.
      </Typography>
    </Box>
  );
}

export default AmericanaTournamentAdmin;
